import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import {
  Crown,
  Award,
  Medal,
  Trophy,
  Users,
  Ticket,
  Sparkles,
  ArrowLeft,
} from "lucide-react";

export default function Leaderboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const promises = [api.get("/leaderboard?limit=20")];
        if (user) promises.push(api.get("/leaderboard/me"));
        const results = await Promise.all(promises);
        setItems(results[0].data.leaderboard);
        if (results[1]) setMe(results[1].data);
      } catch {}
      setLoading(false);
    })();
  }, [user]);

  const ambassadors = items.filter((i) => i.is_ambassador);
  const rest = items.filter((i) => !i.is_ambassador);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="leaderboard-page">
      {/* Hero header */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 circuit-bg opacity-25" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="absolute inset-0 nasa-grid-bg opacity-50" />

        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-16">
          <div className="flex items-start justify-between mb-8">
            <Logo size={56} withText />
            {user ? (
              <Link
                to="/dashboard"
                data-testid="back-to-dashboard"
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#22ff77]"
              >
                <ArrowLeft className="w-3 h-3" /> Nazad
              </Link>
            ) : (
              <Link
                to="/prijava"
                data-testid="login-from-leaderboard"
                className="border border-[#22ff77] text-[#22ff77] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#22ff77]/10"
              >
                Prijavi se
              </Link>
            )}
          </div>

          <p className="text-xs uppercase tracking-[0.4em] text-zinc-500">Javna lista</p>
          <h1 className="font-display text-4xl md:text-6xl font-bold tracking-wide mt-2">
            Najbolji <span className="text-neon-orange">promoteri</span>
          </h1>
          <p className="text-sm md:text-base text-zinc-400 mt-3 max-w-2xl">
            Top 3 promotera automatski postaju{" "}
            <span className="text-[#22ff77] font-bold uppercase tracking-wider">ambasadori</span> celog
            NAŠA Myner projekta za Adria regiju.
          </p>

          {me && me.rank && (
            <div
              data-testid="my-rank-badge"
              className="mt-6 inline-flex items-center gap-3 border border-[#22ff77]/40 bg-[#22ff77]/5 px-4 py-3 rounded-sm"
            >
              <Trophy className="w-5 h-5 text-[#22ff77]" />
              <div className="text-sm">
                <span className="text-zinc-400">Vaša pozicija: </span>
                <span className="text-[#22ff77] font-display text-xl font-bold">#{me.rank}</span>
                <span className="text-zinc-500 ml-2">· {me.active_referrals} aktivnih</span>
                {me.is_ambassador && (
                  <span className="ml-3 text-[#ff7a18] uppercase tracking-widest text-[10px] font-bold">
                    AMBASADOR
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 space-y-12">
        {loading ? (
          <div className="text-center text-zinc-500 py-20 font-display tracking-widest">UČITAVANJE...</div>
        ) : items.length === 0 ? (
          <div className="text-center text-zinc-500 py-20 text-sm">
            Još uvek nema rangiranih promotera. Budite prvi koji će pozvati prijatelje!
          </div>
        ) : (
          <>
            {/* Ambassadors podium */}
            {ambassadors.length > 0 && (
              <section data-testid="ambassadors-section">
                <div className="flex items-center gap-2 mb-6">
                  <Crown className="w-5 h-5 text-[#ff7a18]" />
                  <h2 className="font-display text-2xl font-bold tracking-wide">
                    Ambasadori <span className="text-neon-orange">Adria regije</span>
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {ambassadors.map((p) => (
                    <AmbassadorCard key={p.user_id} promoter={p} />
                  ))}
                </div>
              </section>
            )}

            {/* Rest of leaderboard */}
            {rest.length > 0 && (
              <section data-testid="rest-leaderboard">
                <div className="flex items-center gap-2 mb-6">
                  <Users className="w-5 h-5 text-[#22ff77]" />
                  <h2 className="font-display text-2xl font-bold tracking-wide">Rang lista</h2>
                </div>

                <div className="nasa-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                          <th className="text-left px-6 py-4">Rang</th>
                          <th className="text-left px-6 py-4">Promoter</th>
                          <th className="text-left px-6 py-4">Država</th>
                          <th className="text-right px-6 py-4">Aktivni</th>
                          <th className="text-right px-6 py-4">Tokeni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rest.map((p) => (
                          <tr key={p.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                            <td className="px-6 py-4 font-display text-xl font-bold text-zinc-400">
                              #{p.rank}
                            </td>
                            <td className="px-6 py-4 font-medium">{p.name}</td>
                            <td className="px-6 py-4 text-zinc-400 text-xs">{p.country || "—"}</td>
                            <td className="px-6 py-4 text-right">
                              <span className="text-[#22ff77] font-bold">{p.active_referrals}</span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className="inline-flex items-center gap-1 text-[#22ff77] font-bold">
                                <Ticket className="w-3.5 h-3.5" /> {p.tokens_earned}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* CTA */}
        {!user && (
          <div className="nasa-card p-8 text-center" data-testid="cta-join">
            <Sparkles className="w-8 h-8 text-[#22ff77] mx-auto mb-3" />
            <h3 className="font-display text-2xl mb-2">Postanite naredni ambasador</h3>
            <p className="text-zinc-400 text-sm mb-5">
              Pridružite se NAŠA Myner Adria zajednici i pozovite prijatelje.
            </p>
            <Link
              to="/registracija"
              data-testid="join-cta-button"
              className="inline-block bg-[#22ff77] text-black font-bold py-3 px-8 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 glow-green"
            >
              Registruj se
            </Link>
          </div>
        )}

        <p className="text-center text-[10px] text-zinc-600 tracking-widest uppercase py-6">
          © {new Date().getFullYear()} NAŠA Myner Adria Region · Lista se ažurira u realnom vremenu
        </p>
      </div>
    </div>
  );
}

function AmbassadorCard({ promoter }) {
  const accent = promoter.rank === 1 ? "#ff7a18" : promoter.rank === 2 ? "#22ff77" : "#22ff77";
  const Icon = promoter.rank === 1 ? Crown : promoter.rank === 2 ? Medal : Award;
  const rankLabel = promoter.rank === 1 ? "Prvi" : promoter.rank === 2 ? "Drugi" : "Treći";

  return (
    <div
      data-testid={`ambassador-rank-${promoter.rank}`}
      className="nasa-card p-6 relative overflow-hidden"
      style={{
        boxShadow:
          promoter.rank === 1
            ? "0 0 0 1px rgba(255,122,24,0.4), 0 0 40px rgba(255,122,24,0.18)"
            : "0 0 0 1px rgba(34,255,119,0.35), 0 0 30px rgba(34,255,119,0.12)",
      }}
    >
      <div className="absolute -top-6 -right-6 opacity-10">
        <Icon className="w-32 h-32" style={{ color: accent }} />
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em]" style={{ color: accent }}>
          <Icon className="w-4 h-4" /> {rankLabel} ambasador
        </div>
        <div className="font-display text-5xl font-bold mt-2" style={{ color: accent }}>
          #{promoter.rank}
        </div>
        <div className="font-display text-xl font-bold mt-3">{promoter.name}</div>
        <div className="text-xs text-zinc-500 mt-1">{promoter.country || "Adria"}</div>

        <div className="grid grid-cols-2 gap-2 mt-5 pt-4 border-t border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Aktivni</div>
            <div className="font-display text-2xl font-bold" style={{ color: accent }}>
              {promoter.active_referrals}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Tokeni</div>
            <div className="font-display text-2xl font-bold text-[#22ff77] flex items-center gap-1">
              <Ticket className="w-4 h-4" /> {promoter.tokens_earned}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
